import { FlowConfiguration } from "../FlowConfiguration";
import { FlowState } from "./FlowState";
import { FlowExecutionData } from "./FlowExecutionData";
import { FlowHistoryType } from "./FlowHistoryType";
import { FlowStepData } from "../Steps/FlowStepData";
import { ExecutionData } from "../../Common/Data/ExecutionData/ExecutionData";
import { ITestingContext } from "../../Common/Testing/ITestingContext";
import { FlowConstants } from "../Utilities/FlowConstants";
import { FlowStepOutputInstructions, FlowStepOutputInstructionType } from "../Structure/FlowStepOutputInstructions";
import { BaseFlowStepRunner } from "../Steps/BaseFlowStepRunner";
import { AppGlueError } from "../../AppGlueError";
import { FlowSequenceCompleteStrategy } from "../Structure/FlowSequenceCompleteStrategy";
import { IUserInfo } from "../../Plugins/DataStorage/IUserInfo";
import {
    FlowBranchTestData,
    FlowSequenceTestContext,
    FlowStepTestContext,
    FlowTestContext,
} from "../Structure/FlowTestingContext";

export class FlowEngine {
    // todo: this is temp
    static executingFlows: { [id: string]: FlowExecutionData } = {};

    static storeFlowExecution(user: IUserInfo, data: FlowExecutionData) {
        this.executingFlows[data.flowExecutionId] = data;
    }

    static getFlowExecutionData(user: IUserInfo, id: string): FlowExecutionData | undefined {
        return this.executingFlows[id];
    }

    // static async storeFlowExecution(user: IUserInfo, data: FlowExecutionData) {
    //   await AppGlueCorePlugins.FlowExecutionPlugin?.saveFlowData(user, data);
    // }
    //
    // static async getFlowExecutionData(
    //   user: IUserInfo,
    //   id: string
    // ): Promise<FlowExecutionData | undefined> {
    //   let d = await AppGlueCorePlugins.FlowExecutionPlugin?.getFlowData(user, id);
    //   return d?.flowExecutionData;
    // }

    static async startFlow(
        user: IUserInfo,
        flow: FlowConfiguration,
        data: ExecutionData,
        testContext?: ITestingContext,
        onComplete?: (
            resultingData: ExecutionData,
            endedInError: boolean,
            errorMessage?: string,
            errorStackTrace?: string,
            errorStep?: FlowStepData
        ) => void
    ): Promise<string> {
        let flowData = new FlowExecutionData(flow._id, data);

        await this.storeFlowExecution(user, flowData);

        // set the onComplete if it exists.
        flowData.onComplete = onComplete;
        let steps = flow.getPrimarySequence().steps;

        let step: FlowStepData;
        if (steps.length === 0) step = flow.getPrimarySequence().endStep;
        else step = steps[0];

        flowData.storeExecutionStart(
            FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID,
            flow.getPrimarySequence()._id,
            FlowHistoryType.SEQUENCE
        );

        let flowTestContext: FlowTestContext;
        let sequenceTestContext: FlowSequenceTestContext | undefined;

        if (testContext) {
            flowTestContext = new FlowTestContext(flow._id, undefined, flow);
            testContext.addTestResultItem(flowTestContext);
            sequenceTestContext = new FlowSequenceTestContext(
                flow.getPrimarySequence()._id,
                undefined,
                flow.getPrimarySequence(),
                flowTestContext
            );
            flowTestContext.addTestResultItem(sequenceTestContext);
        }

        await this.runStep(
            user,
            FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID,
            step,
            flowData,
            undefined,
            sequenceTestContext
        );

        return flowData.flowExecutionId;
    }

    static async stepComplete(
        user: IUserInfo,
        flowData: FlowExecutionData,
        thread: string,
        step: FlowStepData,
        path?: string,
        testContext?: FlowStepTestContext
    ): Promise<void> {
        // record end
        flowData.storeExecutionComplete(thread, step._id, FlowHistoryType.STEP, path);

        if (testContext) {
            testContext.endData = flowData.getExecutionDataForThread(thread).values;
            testContext.endDate = new Date();
        }

        let instruction: FlowStepOutputInstructions;

        if (!path) {
            instruction = new FlowStepOutputInstructions("", step._id);
            instruction.strategy = FlowStepOutputInstructionType.CONTINUE;
        } else {
            let i = step.findOutputInstruction(path);

            if (!i) {
                throw new Error("no instruction found");
            }

            instruction = i;
        }

        let nextStep: FlowStepData | undefined = undefined;
        let sequenceTestContext: FlowSequenceTestContext | undefined = testContext?.sequenceTestContext;

        switch (instruction.strategy) {
            case FlowStepOutputInstructionType.BRANCH:
                if (!instruction.connectedSequenceId) throw new Error("connected sequence not specified");

                let seq = step.sequence?.flow?.getSequence(instruction.connectedSequenceId);

                if (!seq) {
                    throw new Error("connected sequence not found ");
                }

                if (seq.steps.length === 0) throw new Error("no steps in connected sequence");

                nextStep = seq.steps[0];

                break;
            case FlowStepOutputInstructionType.CONTINUE:
                nextStep = step.sequence?.getStepAfter(step);

                if (!nextStep) {
                    // we are at the end of a sequence, need to determine what to do now.
                    if (step.sequence?._id === FlowConstants.END_SEQUENCE) {
                        // we are at end of the end sequence, need to end the flow.
                        await this.processEndFlow(user, flowData);
                    } else if (step.sequence?._id === FlowConstants.ERROR_SEQUENCE) {
                        // we are at end of the error sequence, need to end the flow.
                        await this.processEndFlowInError(
                            user,
                            flowData,
                            flowData.errorMessage ?? "flow error",
                            undefined,
                            flowData.errorStackTrace
                        );
                    } else {
                        if (
                            !step.sequence?.strategy ||
                            step.sequence?.strategy === FlowSequenceCompleteStrategy.END_FLOW
                        ) {
                            let eseq = step.sequence?.flow?.endSequence;

                            if (!eseq) throw new Error("end sequence not found");

                            if (eseq.steps.length !== 0) {
                                nextStep = eseq.steps[0];

                                if (testContext) {
                                    testContext.sequenceTestContext.flowTestContext.addTestResultItem(
                                        new FlowBranchTestData(undefined, step._id, path, eseq._id)
                                    );
                                    sequenceTestContext = new FlowSequenceTestContext(
                                        step._id,
                                        undefined,
                                        eseq,
                                        testContext.sequenceTestContext.flowTestContext
                                    );
                                    testContext.sequenceTestContext.flowTestContext.addTestResultItem(
                                        sequenceTestContext
                                    );
                                }
                            } else {
                                await this.processEndFlow(user, flowData);
                            }
                        } else if (step.sequence?.strategy === FlowSequenceCompleteStrategy.END_FLOW_IN_ERROR) {
                            let erseq = step.sequence?.flow?.errorSequence;

                            if (!erseq) throw new Error("error sequence not found");

                            if (erseq.steps.length !== 0) {
                                flowData.errorMessage = step.sequence.errorMessage;
                                await this.setupErrorData(user, flowData);
                                nextStep = erseq.steps[0];

                                if (testContext) {
                                    testContext.sequenceTestContext.flowTestContext.addTestResultItem(
                                        new FlowBranchTestData(undefined, step._id, path, erseq._id)
                                    );

                                    sequenceTestContext = new FlowSequenceTestContext(
                                        step._id,
                                        undefined,
                                        erseq,
                                        testContext.sequenceTestContext.flowTestContext
                                    );
                                    testContext.sequenceTestContext.flowTestContext.addTestResultItem(
                                        sequenceTestContext
                                    );
                                }
                            } else {
                                await this.processEndFlowInError(user, flowData, step.sequence.errorMessage);
                            }
                        } else {
                            //if  (step.sequence?.strategy === FlowElementCompleteStrategy.LINK) {
                            if (!step.sequence.linkToSequence) throw new Error("connected sequence not specified");

                            let seq = step.sequence?.flow?.getSequence(step.sequence.linkToSequence);

                            if (!seq) throw new Error("connected sequence not found");

                            if (seq.steps.length === 0) throw new Error("no steps in connected sequence");

                            nextStep = seq.steps[0];

                            if (testContext) {
                                testContext.sequenceTestContext.flowTestContext.addTestResultItem(
                                    new FlowBranchTestData(undefined, step._id, path, seq._id)
                                );

                                sequenceTestContext = new FlowSequenceTestContext(
                                    step._id,
                                    undefined,
                                    seq,
                                    testContext.sequenceTestContext.flowTestContext
                                );
                                testContext.sequenceTestContext.flowTestContext.addTestResultItem(sequenceTestContext);
                            }
                        }
                    }
                }

                break;
            case FlowStepOutputInstructionType.END_FLOW:
                let eseq = step.sequence?.flow?.endSequence;

                if (!eseq) throw new Error("end sequence not found");

                if (eseq.steps.length !== 0) {
                    nextStep = eseq.steps[0];

                    if (testContext) {
                        testContext.sequenceTestContext.flowTestContext.addTestResultItem(
                            new FlowBranchTestData(undefined, step._id, path, eseq._id)
                        );
                        sequenceTestContext = new FlowSequenceTestContext(
                            step._id,
                            undefined,
                            eseq,
                            testContext.sequenceTestContext.flowTestContext
                        );
                        testContext.sequenceTestContext.flowTestContext.addTestResultItem(sequenceTestContext);
                    }
                } else {
                    await this.processEndFlow(user, flowData);
                }

                break;
            case FlowStepOutputInstructionType.THROW_EXCEPTION:
                let erseq = step.sequence?.flow?.errorSequence;

                if (!erseq) throw new Error("error sequence not found");

                flowData.errorStep = step;
                flowData.errorMessage = instruction.errorMessage;

                await this.setupErrorData(user, flowData);

                if (erseq.steps.length !== 0) {
                    nextStep = erseq.steps[0];

                    if (testContext) {
                        testContext.sequenceTestContext.flowTestContext.addTestResultItem(
                            new FlowBranchTestData(undefined, step._id, path, erseq._id)
                        );
                        sequenceTestContext = new FlowSequenceTestContext(
                            step._id,
                            undefined,
                            erseq,
                            testContext.sequenceTestContext.flowTestContext
                        );
                        testContext.sequenceTestContext.flowTestContext.addTestResultItem(sequenceTestContext);
                    }
                } else {
                    await this.processEndFlowInError(user, flowData, instruction.errorMessage);
                }
                break;
        }

        if (nextStep) {
            if (step.sequence?._id === FlowConstants.ERROR_SEQUENCE) {
                await this.runStep(
                    user,
                    FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID,
                    nextStep,
                    flowData,
                    step,
                    sequenceTestContext
                );
            } else {
                await this.runStep(user, thread, nextStep, flowData, step, sequenceTestContext);
            }
        }
    }

    static async getFlowState(user: IUserInfo, id: string): Promise<FlowState> {
        let exData = await this.getFlowExecutionData(user, id);

        if (!exData) throw new Error("Flow Engine Data Not Found: " + id);

        // todo: fill this in.
        return FlowState.COMPLETED;
    }

    static async getFlowResultData(user: IUserInfo, id: string): Promise<ExecutionData> {
        let exData = await this.getFlowExecutionData(user, id);

        if (!exData) throw new Error("Flow Engine Data Not Found: " + id);

        return exData.getExecutionDataForThread(FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID);
    }

    static async cancelFlow(_user: IUserInfo, _id: string): Promise<void> {
        // removes flow execution from being current
    }

    static async clearFlow(_user: IUserInfo, _id: string): Promise<void> {
        // removes flow execution from data storage
    }

    static async restartFlowAfterStep(
        _user: IUserInfo,
        _id: string,
        _stepId: string,
        _startWithData: FlowExecutionData,
        _startWithPath: string
    ): Promise<void> {
        // when a step is done executing, it will call this method.  it needs to have all the data that is present
        // at that point to restart with.
        // this method might be called when moving from front end to back end
        // or when a call like a task is complete.
    }

    private static async runStep(
        user: IUserInfo,
        thread: string,
        step: FlowStepData,
        flowData: FlowExecutionData,
        priorStep?: FlowStepData,
        testContext?: FlowSequenceTestContext
    ): Promise<void> {
        if (priorStep) {
            if (priorStep.sequence?._id !== step.sequence?._id) {
                // if step is in a different sequence, put sequence in history
                //   and record the end of this sequence
                flowData.storeExecutionComplete(thread, priorStep.sequence!._id, FlowHistoryType.SEQUENCE);

                //   and record the start of new sequence
                flowData.storeExecutionStart(
                    FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID,
                    step.sequence!._id,
                    FlowHistoryType.SEQUENCE
                );
            }
        }

        // this runs the first step
        flowData.storeExecutionStart(thread, step._id, FlowHistoryType.STEP);

        let flowStepTestData: FlowStepTestContext | undefined;

        if (testContext) {
            flowStepTestData = new FlowStepTestContext(step._id, undefined, step, testContext);
            flowStepTestData.startData = flowData.getExecutionDataForThread(thread).values;
            testContext.addTestResultItem(flowStepTestData);
        }

        try {
            let runner: BaseFlowStepRunner = step.getRunner();
            if (!runner) throw new AppGlueError("could not find runner for " + step.__type);
            await runner.start(user, thread, flowData.getThreadData(thread).executionData, flowData, flowStepTestData);
        } catch (e) {
            if (flowStepTestData) {
                flowStepTestData.isError = true;
                flowStepTestData.result = e.toString();
            }

            if (flowData.errorMessage) {
                // this is a secondary error
            } else {
                if (e instanceof Error) {
                    flowData.errorMessage = e.message;
                    flowData.errorStackTrace = e.stack;
                    flowData.errorStep = step;
                } else {
                    flowData.errorMessage = e.toString();
                    flowData.errorStep = step;
                }
            }

            if (flowData.errorMessage === undefined) {
                flowData.errorMessage = "No error message";
            }

            if (
                step.sequence?._id === FlowConstants.ERROR_SEQUENCE ||
                step.sequence!.flow!.getSequence(FlowConstants.ERROR_SEQUENCE)!.steps.length === 0
            ) {
                // we are already in error sequence (or) there is nothing in error sequence, so we must just exit.
                await this.processEndFlowInError(user, flowData, flowData.errorMessage, step, flowData.errorStackTrace);
            } else {
                await this.setupErrorData(user, flowData);

                await this.runStep(
                    user,
                    FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID,
                    step.sequence!.flow!.getSequence(FlowConstants.ERROR_SEQUENCE)!.steps[0],
                    flowData,
                    priorStep
                );
            }
        }
    }

    private static async setupErrorData(user: IUserInfo, flowData: FlowExecutionData): Promise<void> {
        let mainData = flowData.getExecutionDataForThread(FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID);
        mainData.set(FlowConstants.ERROR_MESSAGE_DATA_NAME, flowData.errorMessage);
        mainData.set(FlowConstants.ERROR_STACK_DATA_NAME, flowData.errorStackTrace);
        mainData.set(FlowConstants.ERROR_STEP_ID, flowData.errorStep?._id);
        mainData.set(FlowConstants.ERROR_STEP_NAME, flowData.errorStep?.name);
        mainData.set(FlowConstants.ERROR_STEP_TYPE, flowData.errorStep?.__type);
    }

    private static async processEndFlow(
        user: IUserInfo,
        data: FlowExecutionData,
        flowTestContext?: FlowTestContext
    ): Promise<void> {
        // do end flow stuff.

        if (flowTestContext) {
            flowTestContext.endData = data.getExecutionDataForThread(
                FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID
            ).values;
            flowTestContext.result = "Completed";
            flowTestContext.endDate = new Date();
        }

        data.state = FlowState.COMPLETED;

        if (data.onComplete)
            data.onComplete(data.getThreadData(FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID).executionData, false);
    }

    private static async processEndFlowInError(
        user: IUserInfo,
        data: FlowExecutionData,
        errorMesage: string,
        step?: FlowStepData,
        stackTrace?: string,
        flowTestContext?: FlowTestContext
    ): Promise<void> {
        // do end error stuff
        data.state = FlowState.COMPLETED_WITH_ERROR;
        data.errorMessage = errorMesage;
        data.errorStep = step;
        data.errorStackTrace = stackTrace;

        if (flowTestContext) {
            flowTestContext.isError = true;
            flowTestContext.result = errorMesage;
            flowTestContext.endDate = new Date();
        }

        if (data.onComplete)
            data.onComplete(
                data.getThreadData(FlowConstants.FLOW_EXECUTION_MAIN_THREAD_ID).executionData,
                true,
                errorMesage,
                stackTrace,
                step
            );
    }
}
